import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

export const Reveal = ({ children, delay = 0, className = "", y = 48 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ caption, title, description, align = "left", testId }) => (
  <div className={`mb-14 sm:mb-20 ${align === "center" ? "text-center mx-auto max-w-3xl" : "max-w-3xl"}`} data-testid={testId}>
    <Reveal>
      <p className="text-xs font-mono uppercase tracking-[0.3em] text-indigo-400 mb-4">{caption}</p>
    </Reveal>
    <Reveal delay={0.1}>
      <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-white leading-[1.08]">{title}</h2>
    </Reveal>
    {description && (
      <Reveal delay={0.2}>
        <p className="mt-5 text-sm sm:text-base text-zinc-400 leading-relaxed">{description}</p>
      </Reveal>
    )}
  </div>
);

const formatCompact = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return `${n}`;
};

export const CountUp = ({ value, duration = 1.6, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(formatCompact(Math.round(value * eased)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};
